if (!customElements.get('lac-lightwrap-reels')) {
  customElements.define('lac-lightwrap-reels', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.track = this.querySelector('.lw-reels-track');
      this.cards = Array.from(this.querySelectorAll('.lw-reel'));
      // Shuffle once per page instance, before loading any video sources.
      if (!this.shuffled) {
        for (let i = this.cards.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [this.cards[i], this.cards[j]] = [this.cards[j], this.cards[i]];
        }
        this.track.append(...this.cards);
        this.shuffled = true;
      }
      this.videos = this.cards.map(card => card.querySelector('video'));
      this.visible = new Set();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches || !!navigator.connection?.saveData;
      this.global = this.querySelector('[data-global]');
      this.previous = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.querySelector('.lw-reels-navigation').hidden = false;
      this.cards.forEach(card => {
        const video = card.querySelector('video');
        card.querySelector('.lw-reel-controls').hidden = false;
        video.muted = true;
        video.addEventListener('loadeddata', () => card.classList.add('is-ready'), options);
        video.addEventListener('play', () => this.updateCard(card), options);
        video.addEventListener('pause', () => this.updateCard(card), options);
        video.addEventListener('volumechange', () => this.updateCard(card), options);
        card.querySelector('[data-playback]').addEventListener('click', () => {
          card.dataset.manualPaused = video.paused ? 'false' : 'true';
          if (video.paused) this.play(video); else video.pause();
        }, options);
        card.querySelector('[data-sound-toggle]').addEventListener('click', () => {
          const unmute = video.muted;
          this.videos.forEach(other => { other.muted = true; other.controls = false; });
          video.muted = !unmute;
          video.controls = unmute;
          if (unmute) { card.dataset.manualPaused = 'false'; this.play(video); }
          this.updateCard(card);
        }, options);
        this.updateCard(card);
      });
      this.global.addEventListener('click', () => {
        this.paused = !this.paused;
        this.cards.forEach(card => { card.dataset.manualPaused = 'false'; });
        this.sync();
      }, options);
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.updateArrows(); });
      }, { passive: true, signal: this.abort.signal });
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => { if(this.motion.matches) this.paused = true; this.sync(); }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio >= .5) this.visible.add(entry.target);
            else this.visible.delete(entry.target);
          });
          this.sync();
        }, { threshold: [0, .5] });
        this.cards.forEach(card => this.observer.observe(card));
      } else { this.paused = true; }
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => this.updateArrows());
        this.resize.observe(this.track);
      }
      this.sync();
      this.updateArrows();
    }
    play(video) {
      if (!video.getAttribute('src')) video.src = video.dataset.src;
      video.play().catch(() => this.updateCard(video.closest('.lw-reel')));
    }
    sync() {
      this.global.textContent = this.paused ? this.dataset.play : this.dataset.pause;
      this.global.setAttribute('aria-pressed', String(this.paused));
      this.cards.forEach(card => {
        const video = card.querySelector('video');
        if (!document.hidden && !this.paused && this.visible.has(card) && card.dataset.manualPaused !== 'true') this.play(video);
        else { video.pause(); video.muted = true; video.controls = false; }
      });
    }
    updateCard(card) {
      const video = card.querySelector('video');
      const title = video.getAttribute('aria-label');
      const play = card.querySelector('[data-playback]');
      play.setAttribute('aria-label', (video.paused ? this.dataset.play : this.dataset.pause) + ': ' + title);
      play.querySelector('span').textContent = video.paused ? '▶' : 'Ⅱ';
      const sound = card.querySelector('[data-sound-toggle]');
      sound.setAttribute('aria-label', (video.muted ? this.dataset.sound : this.dataset.mute) + ': ' + title);
      sound.setAttribute('aria-pressed', String(!video.muted));
      card.classList.toggle('is-audible', !video.muted);
    }
    move(direction) {
      const step = this.cards[0]?.getBoundingClientRect().width || this.track.clientWidth;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (step + gap), behavior: this.motion.matches ? 'auto' : 'smooth' });
    }
    updateArrows() {
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.videos?.forEach(video => video.pause());
    }
  });
}
